import { QueryField, Operator, QueryGroup, QueryRule, BackendQueryFormat, BackendQueryRule, BackendQueryGroup, SavedQueryWithConditions, SavedQueryConditionRule, SavedQueryAPI, QuerySource } from '../types/QueryBuilder';

export const SIMPLE_QUERY_CATEGORY = 'SIMPLE_QUERY';

export const getSimpleQueryFields = (fields: QueryField[]): QueryField[] =>
    fields.filter(field => field.Categorie === SIMPLE_QUERY_CATEGORY);

// Any other value (missing, 'SIMPLE', 'SIMPLE_QUERY', ...) is treated as the simple query builder.
const ADVANCED_SOURCE_VALUES = new Set(['ADVANCED', 'DATAQUERYSERVICE']);

export const isAdvancedQuerySource = (value: unknown): boolean =>
    ADVANCED_SOURCE_VALUES.has(String(value ?? '').trim().toUpperCase());

export const toQuerySource = (value: unknown): QuerySource =>
    isAdvancedQuerySource(value) ? 'ADVANCED' : 'SIMPLE';

export const getOperatorsForField = (field: QueryField | undefined): Operator[] => {
    if (!field) {
        return [
            { value: '=', label: 'equals' },
            { value: '!=', label: 'not equals' }
        ];
    }

    if (field.datatype === 'NUMBER' || field.inputType === 'number') {
        return [
            { value: '=', label: 'equals' },
            { value: '!=', label: 'not equals' },
            { value: '>', label: 'greater than' },
            { value: '<', label: 'less than' },
            { value: '>=', label: 'greater or equal' },
            { value: '<=', label: 'less or equal' },
            { value: 'between', label: 'between' }
        ];
    }

    if (field.datatype === 'DATERANGE' || field.inputType === 'date') {
        return [
            { value: '=', label: 'equals' },
            { value: '!=', label: 'not equals' },
            { value: '<', label: 'before' },
            { value: '>', label: 'after' },
            { value: 'between', label: 'between' }
        ];
    }

    if (field.valueEditorType === 'select' || 
        field.valueEditorType === 'multiselect' || 
        field.valueEditorType === 'checkbox') {
        return [
            { value: '=', label: 'equals' },
            { value: '!=', label: 'not equals' },
            { value: 'in', label: 'in' },
            { value: 'not in', label: 'not in' }
        ];
    }

    return [
        { value: '=', label: 'equals' },
        { value: '!=', label: 'not equals' },
        { value: 'contains', label: 'contains' },
        { value: 'not contains', label: 'does not contain' }
    ];
};

export const getFieldsByCategory = (fields: QueryField[], category: string): QueryField[] => {
    return fields.filter(field => field.Categorie === category);
};

export const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const formatQueryDescription = (groups: QueryGroup[], fields: QueryField[]): string => {
    if (!groups?.length || !groups.some(g => g.rules?.length)) return 'No rules defined';

    const formatRule = (rule: QueryRule): string => {
        const field = fields.find(f => f.name === rule.field && (!rule.category || f.Categorie === rule.category));
        if (!field) return '';
        
        const operator = getOperatorsForField(field).find(op => op.value === rule.operator);
        let valueText = rule.value;
        
        if (field.values && rule.value) {
            valueText = Array.isArray(rule.value)
                ? rule.value.map((v: string) => field.values?.find(fv => fv.name === v)?.label || v).join(', ')
                : field.values.find(fv => fv.name === String(rule.value))?.label || rule.value;
        }
        
        return rule.operator === 'between' && rule.valueTo
            ? `${field.label} ${operator?.label} ${rule.value} et ${rule.valueTo}`
            : `${field.label} ${operator?.label} ${valueText}`;
    };

    const descriptions = groups
        .filter(g => g.rules?.length)
        .map(g => g.rules.map(formatRule).filter(Boolean).join(' AND '))
        .filter(Boolean)
        .map(rules => `(${rules})`);

    return descriptions.length > 1 ? descriptions.join(' OR ') : descriptions[0] || 'No rules defined';
};

const hasValue = (value: QueryRule['value'] | QueryRule['valueTo'] | QueryRule['notValue']): boolean => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some(item => String(item).trim() !== '');
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

export const isRuleValid = (rule: QueryRule): boolean => {
    if (!rule.field || !rule.operator) return false;
    // A dual in/notIn field (see notValue on QueryRule) is valid as soon as either list is filled,
    // even if the primary `value` list (the operator's own side) is empty.
    if (!hasValue(rule.value) && !hasValue(rule.notValue)) return false;
    if (rule.operator === 'between') {
        if (typeof rule.value === 'string' && rule.value.includes('|')) {
            const [min, max] = rule.value.split('|');
            // CUSTOMER_ACCOUNT (Compteur fidélité) accepts an open-ended range: min-only ("plus de")
            // or max-only ("moins de") is valid, unlike other between fields which need both bounds.
            return rule.field === 'CUSTOMER_ACCOUNT'
                ? min.trim() !== '' || max.trim() !== ''
                : min.trim() !== '' && max.trim() !== '';
        }
        return hasValue(rule.valueTo);
    }
    return true;
};

export const isGroupValid = (group: QueryGroup): boolean => {
    return group.rules?.length > 0 && group.rules.every(isRuleValid);
};

export const normalizeQueryGroups = (groups: QueryGroup[]): QueryGroup[] => {
    return groups
        .map(group => ({
            ...group,
            rules: group.rules.filter(isRuleValid)
        }))
        .filter(group => group.rules.length > 0);
};

export const convertGroupsToBackendFormat = (groups: QueryGroup[]): BackendQueryFormat => {
    // A rule with both `value` (its own operator's list) and `notValue` (the opposite list) becomes
    // two backend rules — e.g. CUSTOMER_IDENTIFIER "in" (a,b) and "not in" (c,d) — combined with AND
    // by the surrounding group, exactly like two independently-added rules on the same field.
    const formatRule = (rule: QueryRule): BackendQueryRule[] => {
        if (rule.operator === 'in' || rule.operator === 'not in') {
            const toValues = (value: QueryRule['value']) => Array.isArray(value)
                ? value.map(v => String(v).trim()).filter(Boolean)
                : String(value ?? '').split(',').map(v => v.trim()).filter(Boolean);

            const rules: BackendQueryRule[] = [];
            const primaryValues = toValues(rule.value);
            if (primaryValues.length > 0) {
                rules.push({ field: rule.field || '', operator: rule.operator, value: primaryValues });
            }
            if (hasValue(rule.notValue)) {
                const notOperator = rule.operator === 'in' ? 'not in' : 'in';
                rules.push({ field: rule.field || '', operator: notOperator, value: toValues(rule.notValue) });
            }
            return rules.length > 0 ? rules : [{ field: rule.field || '', operator: rule.operator, value: [] }];
        }

        let value: string = '';
        let valueTo: string | undefined;

        if (rule.value !== null && rule.value !== undefined) {
            if (Array.isArray(rule.value)) {
                value = rule.value.map(item => String(item).trim()).filter(Boolean).join(',');
            } else {
                value = String(rule.value);
            }
        }

        if (rule.operator === 'between' && typeof value === 'string' && value.includes('|')) {
            const parts = value.split('|');
            value = parts[0] ?? '';
            valueTo = parts[1] ?? '';
        } else if (rule.operator === 'between' && rule.valueTo) {
            valueTo = String(rule.valueTo);
        }

        return [{
            field: rule.field || '',
            operator: rule.operator || '=',
            value,
            ...(valueTo !== undefined && { valueTo })
        }];
    };

    const validGroups = normalizeQueryGroups(groups);

    if (validGroups.length === 0) {
        return { combinator: 'and', rules: [] };
    }

    return validGroups.length > 1
        ? { combinator: 'or', rules: validGroups.map(g => ({ combinator: 'and', rules: g.rules.flatMap(formatRule) })) }
        : { combinator: 'and', rules: validGroups[0].rules.flatMap(formatRule) };
};

export const convertSavedQueryToGroups = (savedQuery: SavedQueryAPI | SavedQueryWithConditions): QueryGroup[] => {
    if (!savedQuery?.conditions) {
        return [{ id: generateId(), combinator: 'AND', rules: [] }];
    }

    let conditions = savedQuery.conditions;
    if (typeof conditions === 'string') {
        try {
            conditions = JSON.parse(conditions);
        } catch (e) {
            return [{ id: generateId(), combinator: 'AND', rules: [] }];
        }
    }

    if (Array.isArray(conditions) && conditions.length > 0) {
        const firstCondition = conditions[0] as SavedQueryConditionRule;
        
        if (firstCondition?.rules) {
            // Two conditions on the same field (one 'in', one 'not in') were saved as two separate
            // backend rules by convertGroupsToBackendFormat's dual-list expansion — merged back here
            // into one QueryRule (value + notValue) so the simple builder's one-rule-per-field lookup
            // (rules.find(r => r.field === field.name)) doesn't silently drop the second one.
            const mergeDualListRules = (rules: QueryRule[]): QueryRule[] => {
                const merged: QueryRule[] = [];
                const indexByField = new Map<string, number>();
                rules.forEach(rule => {
                    const isPairable = !!rule.field && (rule.operator === 'in' || rule.operator === 'not in');
                    const existingIndex = isPairable ? indexByField.get(rule.field!) : undefined;
                    if (isPairable && existingIndex !== undefined) {
                        const existing = merged[existingIndex];
                        const existingValues = Array.isArray(existing.value) ? existing.value.map(String) : [];
                        const incomingValues = Array.isArray(rule.value) ? rule.value.map(String) : [];
                        merged[existingIndex] = existing.operator === 'in'
                            ? { ...existing, value: existingValues, notValue: incomingValues }
                            : { ...existing, operator: 'in', value: incomingValues, notValue: existingValues };
                        return;
                    }
                    merged.push(rule);
                    if (isPairable) indexByField.set(rule.field!, merged.length - 1);
                });
                return merged;
            };

            const mapRule = (rule: SavedQueryConditionRule): QueryRule => {
                let processedValue: string | string[] | number | null | undefined = rule.value;
                if ((rule.operator === 'in' || rule.operator === 'not in') && rule.value !== undefined && rule.value !== null) {
                    if (!Array.isArray(rule.value)) {
                        if (typeof rule.value === 'string' && rule.value.includes(',')) {
                            processedValue = rule.value.split(',').map(v => v.trim());
                        } else {
                            processedValue = typeof rule.value === 'string' ? [rule.value] : [String(rule.value)];
                        }
                    } else {
                        processedValue = rule.value;
                    }
                }
                
                return {
                    id: generateId(),
                    field: rule.field,
                    operator: rule.operator,
                    value: processedValue,
                    ...(rule.valueTo && { valueTo: rule.valueTo })
                };
            };

            if (firstCondition.combinator === 'or' && firstCondition.rules) {
                const allSameFieldEquals = (firstCondition.rules?.length ?? 0) > 1 &&
                    firstCondition.rules!.every((r: SavedQueryConditionRule) => r.field && r.operator === '=' && r.field === (firstCondition.rules![0] as SavedQueryConditionRule).field);

                if (allSameFieldEquals) {
                    const firstRule = firstCondition.rules[0] as SavedQueryConditionRule;
                    return [{
                        id: generateId(),
                        combinator: 'AND',
                        rules: [{
                            id: generateId(),
                            field: firstRule.field,
                            operator: 'in',
                            value: firstCondition.rules.map((r: SavedQueryConditionRule) => String(r.value)),
                        }]
                    }];
                }

                return firstCondition.rules.map((g: SavedQueryConditionRule): QueryGroup => ({
                    id: generateId(),
                    combinator: 'AND',
                    rules: mergeDualListRules(g.rules?.map(mapRule) || [])
                }));
            }

            if (firstCondition.combinator === 'and' && firstCondition.rules) {
                const firstRule = firstCondition.rules[0] as SavedQueryConditionRule;

                const allSameFieldNotEquals = firstCondition.rules.length > 1 &&
                    firstCondition.rules.every((r: SavedQueryConditionRule) => r.field && r.operator === '!=' && r.field === firstRule.field);

                if (allSameFieldNotEquals) {
                    return [{
                        id: generateId(),
                        combinator: 'AND',
                        rules: [{
                            id: generateId(),
                            field: firstRule.field,
                            operator: 'not in',
                            value: firstCondition.rules.map((r: SavedQueryConditionRule) => String(r.value)),
                        }]
                    }];
                }

                if (firstRule?.combinator === 'or' && firstRule.rules) {
                    return firstRule.rules.map((g: SavedQueryConditionRule): QueryGroup => ({
                        id: generateId(),
                        combinator: 'AND',
                        rules: mergeDualListRules(g.rules?.map(mapRule) || [])
                    }));
                }

                return [{
                    id: generateId(),
                    combinator: 'AND',
                    rules: mergeDualListRules(firstCondition.rules.map(mapRule))
                }];
            }

            const mainRule = firstCondition.rules[0] as SavedQueryConditionRule;
            if (mainRule?.field && mainRule?.operator) {
                return [{
                    id: generateId(),
                    combinator: 'AND',
                    rules: [mapRule(mainRule)]
                }];
            }
        }
    }

    return [{ id: generateId(), combinator: 'AND', rules: [] }];
};
